import React, { useState } from 'react';
import { Mail, Phone, Smartphone, ArrowRight, Check } from 'lucide-react';
import PageLayout from '../components/PageLayout';

const TEAM_SIZES = ['1–5 agents', '6–20 agents', '21–50 agents', '51–200 agents', '200+ agents'];
const CONTACT_EMAIL = 'hello@waslx.co.in';
const CONTACT_PHONE = '+91 7700018187';
const FORMSPREE_URL = 'https://formspree.io/f/mbdqvjvb';

const CHANNELS = [
  { icon: Mail,       accent: 'accent-1', title: 'Email us',           sub: 'Fastest for non-urgent questions', value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
  { icon: Phone,      accent: 'accent-4', title: 'Call us',            sub: 'Mon–Fri, 9am–6pm IST',             value: CONTACT_PHONE, href: 'tel:+917700018187' },
  { icon: Smartphone, accent: 'accent-5', title: 'Download the app',   sub: 'Direct APK — Android only',        value: 'Download APK', href: '/waslx.apk', download: true },
];

const NEXT_STEPS = [
  'We review your message within one business day',
  'A product specialist contacts you to understand your setup',
  'We run a demo against your real use case',
  'You start your one-month free beta — no card needed',
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', company: '', email: '', teamSize: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...form, _subject: `WASLX Enquiry — ${form.company}` }),
      });
      const data = await res.json();
      if (res.ok && !data.error) {
        setSubmitted(true);
      } else {
        setError(data.error || `Something went wrong. Please email us at ${CONTACT_EMAIL}`);
      }
    } catch {
      setError(`Could not send message. Please email us at ${CONTACT_EMAIL}`);
    }
    setSubmitting(false);
  };

  return (
    <PageLayout
      title="Contact Us — Book a Demo"
      description="Book a personalised WASLX demo for your team. Talk to our product specialists about your outbound calling setup. Start your free 1-month beta today — no credit card needed."
      path="/contact"
    >
      {/* The navbar is fixed and the hero was what used to clear it, so its
          72px moves here — ADDED to the section's own padding rather than
          replacing it. A bare pt-[72px] overrides .section's padding-block
          and lands the form card flush against the nav. */}
      <section className="section section--muted pt-[calc(22px+clamp(56px,7vw,104px))]">
        <div className="container-page">
          <div className="grid gap-6 lg:grid-cols-5">
            <div className="card lg:col-span-3">
              {submitted ? (
                <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
                  <span className="mb-6 flex h-16 w-16 items-center justify-center rounded-pill bg-success-tint text-success-strong">
                    <Check className="h-8 w-8" />
                  </span>
                  <h2 className="text-2xl font-bold text-content">Message sent</h2>
                  <p className="mt-3 max-w-xs text-content-muted">
                    We will be in touch within one business day. Keep an eye on{' '}
                    {form.email || 'your inbox'}.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-content">Send us a message</h2>
                  <p className="mt-2 text-content-muted">
                    We get back to you within one business day.
                  </p>

                  <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label className="form-label" htmlFor="name">
                          Full name <span className="req">*</span>
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Waslx Demo"
                          className="form-input"
                        />
                      </div>
                      <div>
                        <label className="form-label" htmlFor="company">
                          Company <span className="req">*</span>
                        </label>
                        <input
                          id="company"
                          name="company"
                          type="text"
                          required
                          value={form.company}
                          onChange={handleChange}
                          placeholder="Acme BPO Pvt. Ltd."
                          className="form-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label" htmlFor="email">
                        Work email <span className="req">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="rajesh@company.com"
                        className="form-input"
                      />
                    </div>

                    <div>
                      <label className="form-label" htmlFor="teamSize">Team size</label>
                      <select
                        id="teamSize"
                        name="teamSize"
                        value={form.teamSize}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">Select team size</option>
                        {TEAM_SIZES.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="form-label" htmlFor="message">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Tell us about your calling operations, or what you would like to see in the demo…"
                        className="form-input"
                      />
                    </div>

                    {error && <p className="form-error">{error}</p>}

                    <button type="submit" disabled={submitting} className="btn-pill btn-pill--primary btn-pill--block">
                      {submitting ? (
                        'Sending…'
                      ) : (
                        <>
                          Send message
                          <ArrowRight className="h-[18px] w-[18px]" />
                        </>
                      )}
                    </button>

                    <p className="text-center text-sm text-content-muted">
                      By submitting you agree to our privacy policy. We do not spam.
                    </p>
                  </form>
                </>
              )}
            </div>

            <div className="space-y-4 lg:col-span-2">
              {CHANNELS.map(channel => {
                const Icon = channel.icon;
                return (
                  <div key={channel.title} className={`${channel.accent} card card--hover flex items-start gap-4`}>
                    <span className="icon-tile">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-bold text-content">{channel.title}</p>
                      <p className="mt-0.5 text-sm text-content-muted">{channel.sub}</p>
                      <a
                        href={channel.href}
                        download={channel.download || undefined}
                        className="focus-ring mt-2 inline-block rounded-sm font-bold text-accent-strong"
                      >
                        {channel.value}
                      </a>
                    </div>
                  </div>
                );
              })}

              <div className="accent-brand card card--tint">
                <p className="font-bold text-content">What happens next?</p>
                <ol className="mt-4 space-y-3">
                  {NEXT_STEPS.map((step, i) => (
                    <li key={step} className="flex items-start gap-3 text-sm text-content">
                      <span className="mt-px flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-pill bg-surface text-2xs font-bold text-accent-strong">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Contact;
